import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, Conversation, Message, Profile } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";
import { Link } from "react-router-dom";

type ConversationWithProvider = Conversation & {
  provider: Profile;
};

type MessageWithSender = Message & {
  sender: Profile;
};

export default function MyMessages() {
  const { currentUser } = useApp();
  const [conversations, setConversations] = useState<ConversationWithProvider[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageWithSender[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selected = conversations.find((c) => c.id === selectedId);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load conversations
  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    supabase
      .from("conversations")
      .select("*, provider:profiles!provider_id(*)")
      .eq("consumer_id", currentUser.id)
      .order("last_message_at", { ascending: false, nullsFirst: false })
      .then(({ data }) => {
        setConversations((data as ConversationWithProvider[]) || []);
        if (data && data.length > 0 && !selectedId) {
          setSelectedId(data[0].id);
        }
        setLoading(false);
      });
  }, [currentUser]);

  // Load messages for selected conversation
  useEffect(() => {
    if (!selectedId) return;
    supabase
      .from("messages")
      .select("*, sender:profiles!sender_id(*)")
      .eq("conversation_id", selectedId)
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        setMessages((data as MessageWithSender[]) || []);
        setTimeout(scrollToBottom, 100);
      });

    // Mark consumer messages as read
    if (currentUser) {
      supabase
        .from("conversations")
        .update({ consumer_unread: 0 })
        .eq("id", selectedId)
        .eq("consumer_id", currentUser.id)
        .then(() => {
          setConversations((prev) =>
            prev.map((c) => (c.id === selectedId ? { ...c, consumer_unread: 0 } : c))
          );
        });
    }
  }, [selectedId, currentUser, scrollToBottom]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!selectedId) return;
    const channel = supabase
      .channel(`messages:${selectedId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${selectedId}` },
        async (payload) => {
          const { data } = await supabase
            .from("messages")
            .select("*, sender:profiles!sender_id(*)")
            .eq("id", payload.new.id)
            .maybeSingle();
          if (data) {
            setMessages((prev) => [...prev, data as MessageWithSender]);
            setTimeout(scrollToBottom, 100);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedId, scrollToBottom]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedId || !currentUser || sending) return;
    setSending(true);
    const content = newMessage.trim();
    setNewMessage("");

    const { data } = await supabase
      .from("messages")
      .insert({
        conversation_id: selectedId,
        sender_id: currentUser.id,
        content,
        is_read: false,
      })
      .select("*, sender:profiles!sender_id(*)")
      .maybeSingle();

    if (data) {
      setMessages((prev) => [...prev, data as MessageWithSender]);
      setTimeout(scrollToBottom, 100);
    }

    // Update conversation last message
    await supabase
      .from("conversations")
      .update({
        last_message: content,
        last_message_at: new Date().toISOString(),
        provider_unread: (selected?.provider_unread ?? 0) + 1,
      })
      .eq("id", selectedId);

    setConversations((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? { ...c, last_message: content, last_message_at: new Date().toISOString() }
          : c
      )
    );

    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.consumer_unread ?? 0), 0);

  if (!currentUser) {
    return (
      <div className="text-center py-16">
        <p className="text-charcoal/40 font-sans">ログインしてメッセージを確認してください</p>
        <Link to="/login" className="mt-3 inline-block text-teal font-sans hover:underline cursor-pointer">
          ログイン →
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 h-[600px]">
      {/* Thread List */}
      <div className="w-full md:w-72 bg-white rounded-2xl border border-stone-100 overflow-y-auto flex-shrink-0 flex flex-col">
        <div className="p-4 border-b border-stone-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="font-serif text-deep-green text-sm">メッセージ</p>
            {totalUnread > 0 && (
              <span className="bg-gold text-white text-xs rounded-full px-2 py-0.5 font-sans">{totalUnread}件未読</span>
            )}
          </div>
          <p className="text-xs text-charcoal/40 font-sans mt-0.5">購入済み事業者とのやり取り</p>
        </div>

        {loading ? (
          <div className="flex-1 p-4 space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-stone-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <i className="ri-message-3-line text-3xl text-charcoal/20 mb-2 block" />
            <p className="text-xs text-charcoal/40 font-sans">まだメッセージはありません</p>
            <p className="text-xs text-charcoal/30 font-sans mt-1">
              購入後に事業者とやり取りできます
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {conversations.map((conv) => {
              const provider = conv.provider as Profile;
              const avatarSrc = provider?.avatar_url ||
                `https://public.readdy.ai/ai/img_res/891a07511dac53ce3879e54c6c9bc76c.jpg${conv.id.slice(0, 8)}&orientation=squarish`;
              return (
                <button
                  key={conv.id}
                  onClick={() => setSelectedId(conv.id)}
                  className={`w-full text-left p-4 border-b border-stone-100 transition-colors cursor-pointer flex gap-3 ${selectedId === conv.id ? "bg-teal/5" : "hover:bg-stone-50"}`}
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 relative">
                    <img
                      src={avatarSrc}
                      alt={provider?.service_name ?? provider?.name ?? "事業者"}
                      className="w-full h-full object-cover object-top"
                    />
                    {(conv.consumer_unread ?? 0) > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold text-white text-xs rounded-full flex items-center justify-center font-sans">
                        {conv.consumer_unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-sans text-charcoal font-medium truncate">
                      {provider?.service_name ?? provider?.name ?? "事業者"}
                    </p>
                    <p className="text-xs text-charcoal/50 font-sans truncate mt-0.5">
                      {conv.last_message ?? "まだメッセージがありません"}
                    </p>
                    {conv.last_message_at && (
                      <p className="text-xs text-charcoal/30 font-sans mt-1">
                        {new Date(conv.last_message_at).toLocaleDateString("ja-JP", { month: "short", day: "numeric" })}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Chat Window */}
      {selected ? (
        <div className="flex-1 bg-white rounded-2xl border border-stone-100 flex flex-col overflow-hidden min-w-0">
          {/* Header */}
          <div className="p-4 border-b border-stone-100 flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={(selected.provider as Profile)?.avatar_url ||
                  `https://readdy.ai/api/search-image?query=Japanese%20artisan%20portrait%20professional&width=80&height=80&seq=chat-av-${selected.id.slice(0, 8)}&orientation=squarish`}
                alt={(selected.provider as Profile)?.name ?? "事業者"}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm font-medium text-charcoal truncate">
                {(selected.provider as Profile)?.service_name ?? (selected.provider as Profile)?.name}
              </p>
              {selected.purchased_item_title && (
                <p className="text-xs text-charcoal/40 font-sans truncate">{selected.purchased_item_title}</p>
              )}
            </div>
            <Link
              to={`/providers/${(selected.provider as Profile)?.id}`}
              className="text-xs text-teal font-sans hover:underline cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              ページを見る
            </Link>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-charcoal/30">
                <i className="ri-message-3-line text-3xl mb-2 block" />
                <p className="text-sm font-sans">まだメッセージはありません</p>
                <p className="text-xs font-sans mt-1">気軽に質問してみましょう！</p>
              </div>
            )}
            {messages.map((msg) => {
              const isMe = msg.sender_id === currentUser.id;
              return (
                <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                  {!isMe && (
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 self-end">
                      <img
                        src={(selected.provider as Profile)?.avatar_url ||
                          `https://readdy.ai/api/search-image?query=Japanese%20artisan%20portrait&width=60&height=60&seq=msg-s-${msg.id.slice(0, 8)}&orientation=squarish`}
                        alt="provider"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className={`max-w-xs md:max-w-sm rounded-2xl px-4 py-3 ${isMe ? "bg-deep-green text-white rounded-tr-sm" : "bg-stone-100 text-charcoal rounded-tl-sm"}`}>
                    <p className="text-sm font-sans leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-1.5 ${isMe ? "text-white/50" : "text-charcoal/40"}`}>
                      {new Date(msg.created_at).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-stone-100 flex-shrink-0">
            <p className="text-xs text-charcoal/40 font-sans mb-2 text-center">
              このやりとりはTSUGITEの利用規約に基づき監視対象です。
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="メッセージを入力... (Enterで送信)"
                className="flex-1 bg-stone-50 rounded-full px-4 py-2.5 text-sm font-sans text-charcoal outline-none border border-stone-200 focus:border-teal transition-colors"
              />
              <button
                onClick={handleSend}
                disabled={sending || !newMessage.trim()}
                className="w-10 h-10 flex items-center justify-center bg-deep-green text-white rounded-full cursor-pointer hover:bg-teal transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-send-plane-fill" />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 bg-white rounded-2xl border border-stone-100 flex flex-col items-center justify-center text-center p-8">
          <i className="ri-message-3-line text-5xl text-charcoal/10 block mb-4" />
          <p className="text-charcoal/40 font-sans text-sm">会話を選択してください</p>
          {conversations.length === 0 && !loading && (
            <div className="mt-4">
              <p className="text-charcoal/30 font-sans text-xs">講座・教材を購入すると</p>
              <p className="text-charcoal/30 font-sans text-xs mt-0.5">事業者にメッセージを送れます</p>
              <Link to="/providers" className="mt-3 inline-block text-teal text-sm font-sans hover:underline cursor-pointer">
                事業者を探す →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
